import {
    Component,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';

import { BehaviorSubject, filter, Subscription, takeWhile } from 'rxjs';
import Quill from 'quill';
import { MessageService } from 'primeng/api';
import CodeMirror from 'codemirror';
import { isArray, cloneDeep, isEmpty } from 'lodash';

import { Clipboard } from './Quill/clipboard';
import { PlainTextClipboard } from './Quill/plainTextClipboard';
import { FONT_SIZE_WHITE_LIST, IMAGE_FILE_TYPE_REGEX, MAX_FILE_SIZE_BYTES } from './Quill/quill.const';
import { CustomFontStyleAttribute, CUSTOM_FONT_STYLE, FONT_CLASS } from './Quill/styles/custom-font-style.model';

import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { MediaResultModel, MediaServiceErrorCodesEnum } from '../../../../api/mediaService/mediaResults.api.model';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { getReadableFileSize } from '../../../../Core/utils';

import { ColorPickerDialogComponent } from '../../../Dialogs/components/ColorPickerDialog/color-picker-dialog.component';
import { AIRefinementDialogComponent } from '../../../Dialogs/components/AIRefinementDialog/AI-refinement-dialog.component';
import { AIRefinementPopupComponent } from '../../../Dialogs/components/AIRefinementPopup/AI-refinement-popup.component';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { AIHelperDialogComponent } from '../../../Dialogs/components/AIHelperDialog/AI-helper-dialog.component';
import { LinkDialogComponent } from '../../../Dialogs/components/LinkDialog/link-dialog.component';

import { EmailHelper } from '../../../PageBuilder/helpers/email.helper';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ImagesService } from '../../../PageBuilder/services/images.service';
import { CustomDropdownOption, DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { RichTextEditorDefaultStylesModel } from '../../../Forms/models/rich-text-editor-default-styles.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ColorModel } from '../../../Forms/models/color.model';
import { htmlToText } from '../../../Misc/helpers/html/html.helper';
import { QuillAccessibilityHelper } from './Quill/helpers/quill-accessibility.helper';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { LinkModel } from '../../models/link.model';
import { LinkTypeEnum } from '../../enums/link-type.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { translationLanguageOptions } from '../../data/translation-language-options';
import { languageDataLocalizationMap } from '../../../../Core/Localization/language-localization.model';
import { TranslateService } from '../../services/translate.service';
import { TranslationModel } from '../../models/translation.model';
import { FallbackPersonalizationsDialogComponent } from '../../../Dialogs/components/FallbackPersonalizationsDialog/fallback-personalizations-dialog.component';
import { GLOBAL_URIS } from '../../../Misc/helpers/formatting/global-uri.helper';

/* eslint-disable @typescript-eslint/naming-convention */

/** HACK this fixes an issue where package builds think that these are node packages to import, rather than quill extensions */
const quimport = Quill.import.bind(Quill);
const ICONS = quimport('ui/icons');
const SIZE_STYLE = quimport('attributors/style/size');
const ALIGNMENT_STYLE = quimport('attributors/style/align');

Quill.register(FONT_CLASS, true);

const COLOR_STYLE = quimport('attributors/style/color');
const PARCHMENT = quimport('parchment');
const LINK = quimport('formats/link');
/* END HACK */

const ENFORCED_FONT_SIZE_WHEN_NOT_EDITABLE = '12pt';

export const LINE_HEIGHT_CONFIG = {
    scope: PARCHMENT.Scope.BLOCK,
    whitelist: ['1.3', '1.95', '2.6', '3.25', '3.9']
};

const LINE_HEIGHT_STYLE = new PARCHMENT.Attributor.Style('lineheight', 'line-height', LINE_HEIGHT_CONFIG);

ICONS['bold'] = '<span class="agpr-i-font-bold ql-customIcon" aria-hidden="true"></span>';
ICONS['underline'] = '<span class="agpr-i-font-underline ql-customIcon" aria-hidden="true"></span>';
ICONS['italic'] = '<span class="agpr-i-font-italic ql-customIcon" aria-hidden="true"></span>';
ICONS['strikethrough'] = '<span class="fa-regular fa-duotone fa-strikethrough ql-customIcon" aria-hidden="true"></span>';

SIZE_STYLE.whitelist = FONT_SIZE_WHITE_LIST.map(x => x + 'px');

ALIGNMENT_STYLE.whitelist = [...ALIGNMENT_STYLE.whitelist, 'left'];

Quill.register('modules/clipboard', Clipboard);

Quill.register(LINE_HEIGHT_STYLE, true);
Quill.register(COLOR_STYLE, true);
Quill.register(SIZE_STYLE, true);
Quill.register(ALIGNMENT_STYLE, true);
Quill.register(CUSTOM_FONT_STYLE, true);

interface Range {
    index: number;
    length: number;
}

interface HtmlSelection {
    preSelection: string;
    postSelection: string;
    selection: string;
}

@Component({
    selector: 'agpr-rich-text-editor',
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss'],
    providers: [
        getProvider(RichTextEditorComponent),
        {
            provide: ImagesService,
            useClass: ImagesService
        }
    ],
    standalone: false
})
export class RichTextEditorComponent extends ControlValueAccessorModel<string> implements OnInit, OnChanges, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() formatOptions: RichTextEditorDefaultStylesModel;
    @Input() editHTML: boolean = false;
    @Input() enablePersonalizations: boolean = false;
    @Input() enableLineHeight: boolean = true;
    @Input() enableFontColor: boolean = true;
    @Input() enableBackgroundColor: boolean = true;
    @Input() enableHtmlEditor: boolean = true;
    @Input() enableAIHelper: boolean = false;
    @Input() enableAIRefinement: boolean = false;
    @Input() enableFontFamily: boolean = true;
    @Input() enableFontSize: boolean = true;
    @Input() enableInsertImage: boolean = true;
    @Input() enableStrikethrough: boolean = true;
    @Input() enableNumberedList: boolean = true;
    @Input() enableBlockQuote: boolean = true;
    @Input() pastePlainText: boolean = false;
    @Input() enableHeading: boolean = true;
    @Input() enableAlignment: boolean = true;
    @Input() enableIndent: boolean = true;
    @Input() enableDirection: boolean = true;
    @Input() enableTranslation: boolean = false;
    @Input() maxChars: number;
    @Input() aiReferenceId: string;
    @ViewChild('editorElement', {static: false }) editorRef: any;
    @Output() htmlEditChange: EventEmitter<boolean> = new EventEmitter();
    @Output() escapeEditor: EventEmitter<null> = new EventEmitter();
    @Output() insertTextBlock: EventEmitter<string> = new EventEmitter();
    @Input() mergeTagOptions: DropdownOptionModel[];
    mergeTagCustomOptions: CustomDropdownOption[] = [
        {
            label: 'FallbackPersonalizationDialog.heading',
            value: null,
            action: () => this.openEditMergeTag()
        }
    ];
    @Input() isFooter: boolean;

    showQuillEditor: boolean = false;
    viewOnly: boolean = false;
    cachedValue: string;

    richTextIsDirty: boolean = false;

    buttonTypesEnum = ButtonTypesEnum;

    centerAlign: boolean = false;
    rightAlign: boolean = false;

    quillEditorContentStyles: any = {'min-height': '120px'};

    value: string = '';
    config = {};

    quillEditor: Quill;
    tooltip: any;

    currentSelection: any;
    content: any;
    oldText: string = '';

    codeMirrorConfig: CodeMirror.EditorConfiguration;

    selectedColor: string;
    selectedFontBackgroundColor: string;
    selectedScript: string;
    selectedListType: string;
    selectedAlignment: string;
    selectedLink: string;
    isEmpty: boolean = false;

    isBold: boolean = false;
    isItalic: boolean = false;
    isUnderlined: boolean = false;
    isStrikethrough: boolean = false;
    isRTL: boolean = false;
    isBlockQuote: boolean = false;
    refinementModalOpen: boolean = false;

    mousePositionX: number = 0;
    mousePositionY: number = 0;
    mouseMoveListener: any;
    aiButtonClickListener: any;
    toolbarHeight: number = 0;
    elementHeight: number = 0;
    elementWidth: number = 0;

    readonly aiPopupWidth: number = 340;
    readonly aiPopupHeight: number = 390;

    aiButton: any;
    paragraphs: any;

    pTagRegex = /<p(.*?)>/i;
    htmlTagRegex = /<([a-z]*)/i;
    emptyParagraphMatch: string[] = ['', '<p><br/></p>', '<p><br></p>', '<p></p>', '<br/></p>', '<br></p>', '</p>'];
    emptyParagraphMatchText: string[] = ['', '<br/>', '<br>'];

    lineSpacingOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('1.0', '1.3'),
        new DropdownOptionModel('1.5', '1.95'),
        new DropdownOptionModel('2.0', '2.6'),
        new DropdownOptionModel('2.5', '3.25'),
        new DropdownOptionModel('3.0', '3.9')
    ];

    overlayContainer: Element;
    openAiPermission: boolean = false;

    readonly mergeTagsInfoArticle = GLOBAL_URIS.personalizationsHelp;
    readonly footerMergeTagsInfoArticle = 'https://help.agilitypr.com/en/articles/6525391-using-merge-tags-to-edit-the-email-footer';

    mergeTagsInfoTemplate: string = '<span class="agpr-mergeTags-tooltip">' + this.localizationService.transform('Discover.targeting.actions.learnMore') +
    '</span>' + ` <a href="${this.mergeTagsInfoArticle}"` +
    'target="_blank" class="agpr-mergeTags-tooltip-link">' + this.localizationService.transform('Common.forms.richText.usingMergeTags') + '.</a>';

    footerMergeTagsInfoTemplate: string = '<span class="agpr-mergeTags-tooltip">' + this.localizationService.transform('Discover.targeting.actions.learnMore') +
    '</span>' + ` <a href="${this.footerMergeTagsInfoArticle}"` +
    'target="_blank" class="agpr-mergeTags-tooltip-link">' + this.localizationService.transform('Common.forms.richText.usingMergeTags') + '.</a>';


    selectedLanguage: DropdownOptionModel;
    defaultLanguage: DropdownOptionModel;
    defaultLanguages: DropdownOptionModel[] = translationLanguageOptions;

    translation: TranslationModel;
    protected languageSuggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject(this.defaultLanguages);
    isTranslating: boolean = false;

    subscriptions: Subscription[];

    constructor(
        public coreDialogService: CoreDialogService,
        public mediaServiceApi: MediaServiceApi,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public ref: ElementRef,
        public currentUserStateService: CurrentUserStateService,
        public injector: Injector,
        public renderer: Renderer2,
        private configService: ConfigService,
        private translateService: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.codeMirrorConfig = {
            lineNumbers: true,
            mode: 'text/html',
            lineWrapping: true,
            extraKeys: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Esc': this.escapeTextEditor.bind(this)
            }
        };

        if (this.enableTranslation) {
            this.translation = new TranslationModel();
        }

        this.subscriptions = [];
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission =  permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.currentUserStateService.getDataReferenceLanguageTag().subscribe(languageTag => {
                let languageString = 'English (US)';

                if (languageDataLocalizationMap['en-US.'+languageTag]) {
                    languageString = languageDataLocalizationMap['en-US.'+languageTag];
                }

                this.selectedLanguage = this.defaultLanguages.find(lang => lang.label === languageString);
                this.defaultLanguage = {...this.selectedLanguage};
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.defaultLanguages.forEach(option => {
                    option.label = languageDataLocalizationMap[tag + '.' + option.subLabel];
                });
            })
        );
    }

    ngOnChanges(simpleChanges: SimpleChanges) {
        if (simpleChanges.hasOwnProperty('formatOptions')) {
            this.centerAlign = false;
            this.rightAlign = false;

            if (this.formatOptions['text-align'] === 'center') {
                this.centerAlign = true;
            }

            if (this.formatOptions['text-align'] === 'right') {
                this.rightAlign = true;
            }

            this.quillEditorContentStyles = Object.assign({}, {'min-height': '120px'}, this.formatOptions);
        }

        if (simpleChanges.hasOwnProperty('enableFontFamily')) {
            if (this.enableFontFamily === false) {

                const limitedFontStyleAttributeConfig = {
                    scope: PARCHMENT.Scope.INLINE,
                    whitelist: ['Arial, Helvetica, sans-serif']
                };

                const limitedFontStyleAttribute = new CustomFontStyleAttribute('font', 'font-family', limitedFontStyleAttributeConfig);
                Quill.register(limitedFontStyleAttribute, true);
            } else {
                Quill.register(CUSTOM_FONT_STYLE, true);
            }
        }

        if (simpleChanges.hasOwnProperty('enableFontSize')) {
            if (this.enableFontSize === false) {
                SIZE_STYLE.whitelist = [ENFORCED_FONT_SIZE_WHEN_NOT_EDITABLE];
                Quill.register(SIZE_STYLE, true);

            } else {
                SIZE_STYLE.whitelist = FONT_SIZE_WHITE_LIST.map(x => x + 'px');
                Quill.register(SIZE_STYLE, true);
            }
        }

        if (simpleChanges.hasOwnProperty('pastePlainText') && this.pastePlainText) {
            Quill.register('modules/clipboard', PlainTextClipboard);
        }
    }

    escapeTextEditor = function() {
        this.escapeEditor.emit();
    };

    change() {
        this.isEmpty = this.quillEditor.getText().trim().length === 0;
        if (this.isEmpty) {
            requestAnimationFrame(() => {
                QuillAccessibilityHelper.bindToolbarKeyboardNavigation(document, this.quillEditor, this.escapeTextEditor);
            });
        }
        this.richTextIsDirty = true;
        if (this.maxChars && this.value) {
            this.value = this.value.substring(0, this.maxChars);
        }
        this.onModelChange(this.value);
    }

    writeValue(value: any) {
        this.cachedValue = value;
        this.value = value;
    }

    changeToHtmlEditor() {
        this.editHTML = true;
        this.value = EmailHelper.getTextContentStyledForEmail(this.value);
        this.value = this.formatHTML(this.value);
        this.onModelChange(this.value);
        this.htmlEditChange.emit(this.editHTML);
    }

    changeToTextEditor() {
        this.editHTML = false;
        this.htmlEditChange.emit(this.editHTML);
    }

    htmlChange(event) {
        this.value = event;
        if (this.maxChars) {
            this.value = this.value.substring(0, this.maxChars);
        }
        this.onModelChange(this.value);
    }

    editorCreated(editor) {
        if (!this.value) {
            this.value = '';
        }

        this.quillEditor = editor;

        this.tooltip = this.quillEditor.theme.tooltip;
        this.tooltip.edit = this.editLink.bind(this);
        this.quillEditor.setSelection(this.quillEditor.getLength(), 0);

        if (this.quillEditor) {
            this.isEmpty = this.quillEditor.getText().trim().length === 0;
        }

        QuillAccessibilityHelper.applyAccessibilityFix(document, this.quillEditor, this.escapeTextEditor.bind(this));

        if ((this.enableAIHelper || this.enableAIRefinement) && this.openAiPermission) {
            this.setupRefinementHover();
        }
    }

    setupRefinementHover() {
        const editorElement = document.getElementById('agpr_quillTextEditor_element');
        if (!editorElement) {
            return;
        }
        // gettings the toolbar heights & editor heights & with to make sure it doesn't go outside the "box"
        this.toolbarHeight = document.getElementById('agpr_quillTextEditor_toolbar')?.clientHeight || 0;
        this.elementHeight = document.getElementById('agpr_quillTextEditor_element')?.clientHeight || 0;
        this.elementWidth = document.getElementById('agpr_quillTextEditor_element')?.clientWidth || 0;

        // remove all existing listeners
        if (this.mouseMoveListener) {
            this.mouseMoveListener();
        }

        // we need to add mouse listener to know where the last position of the mouse was after text select
        this.mouseMoveListener = this.renderer?.listen(document.getElementById('agpr_quillTextEditor_element'), 'mousemove', (mouseMoveEvent) => {
            this.mousePositionX = mouseMoveEvent.layerX;
            this.mousePositionY = mouseMoveEvent.layerY;
        });

        // get all the paragraphs in the quill editor
        this.paragraphs = document.getElementById('agpr_quillTextEditor_element').getElementsByTagName('p');

        for(let i = 0; i < this.paragraphs.length; i++) {
            this.paragraphs[i].onmouseenter = (hoverEvent: any) => {
                // remove other paragraph buttons
                document.getElementById('agpr_richTextEditor_refinementParagraph')?.remove();

                // if the paragraph actually has text in it
                if (!this.emptyParagraphMatchText.includes(this.paragraphs[i]?.innerHTML)) {
                    this.aiButton = document.createElement('button');
                    this.aiButton.id = 'agpr_richTextEditor_refinementParagraph';
                    this.aiButton.className = 'agpr-richTextEditor-refinementParagraph';

                    this.aiButton.style.top = (hoverEvent?.srcElement?.closest('p')?.['offsetTop'] + this.toolbarHeight) + 'px';
                    this.aiButton.style.left = '0px';

                    this.aiButton.onclick = () => {
                        // remove selection refinement button if clicked
                        document.getElementById('agpr_richTextEditor_refinementButton')?.remove();

                        this.refinementModalOpen = true;

                        // get the index of the start of the paragraph
                        const blot = Quill.find(this.paragraphs[i]);
                        const index = blot.offset(this.quillEditor.scroll);

                        // set the new selection with the index & paragraph length
                        this.quillEditor.setSelection(index, htmlToText(this.paragraphs[i]?.innerHTML).length || 0, 'user');
                        this.quillEditor.focus();

                        this.launchAIRefinementDialog(this.aiButton);
                    };

                    this.aiButton.onmouseenter = () => {
                        this.paragraphs[i]?.classList.add('agpr-richTextEditor-highlightParagraph');
                    };

                    this.aiButton.onmouseout = () => {
                        this.removeHighlightParagraph();
                    };

                    const buttonTextEl = document.createElement('span');
                    buttonTextEl.className = 'fa-agpr-ai';
                    this.aiButton?.appendChild(buttonTextEl);
                    document.getElementById('agpr_quillTextEditor_element').appendChild(this.aiButton);
                }
            };
        }
    }

    removeHighlightParagraph() {
        const els = document.getElementsByClassName('agpr-richTextEditor-highlightParagraph');

        // remove all other agpr-richTextEditor-highlightParagraph classes
        Array.from(els).forEach((el) => {
            el.classList.remove('agpr-richTextEditor-highlightParagraph');
        });
    }

    editorChange(event): void {
        this.currentSelection = event?.editor?.selection;
        if (!this.currentSelection) {
            return;
        }
        const index = this.currentSelection?.savedRange?.index;
        const length = this.currentSelection?.savedRange?.length;
        this.updateSelectionState(index, length);

        if ((this.enableAIHelper || this.enableAIRefinement) && this.openAiPermission) {
            this.setupRefinementHover();
        }
    }

    addRefineButton() {
        // remove all other refinement buttons
        document.getElementById('agpr_richTextEditor_refinementButton')?.remove();

        // sometimes you can just selected a new line, which doesn't even show up as text..
        // but the range length is 1, so the refine button shows up
        let isOnlyNewLine = false;
        if (length === 1) {
            const quillSelection = this.quillEditor?.getSelection();
            if (quillSelection) {
                const selectedContent = this.quillEditor.getContents(quillSelection.index, quillSelection.length);
                if ((selectedContent?.ops?.length === 1) && (selectedContent?.ops?.[0]?.insert === '\n')) {
                    isOnlyNewLine = true;
                }
            }
        }

        // if there is an actual selection & if there modal is not already open
        if (this.currentSelection?.range?.length && !this.refinementModalOpen && !isOnlyNewLine) {
            if (this.aiButtonClickListener) {
                this.aiButtonClickListener();
            }
            const buttonEl = document.createElement('button');
            this.aiButtonClickListener = buttonEl.addEventListener('click', () => {
                this.launchAIRefinementDialog(buttonEl);
            });

            buttonEl.id = 'agpr_richTextEditor_refinementButton';
            buttonEl.className = 'agpr-richTextEditor-refinementButton';

            buttonEl.style.top = Math.max(this.toolbarHeight, Math.min(this.elementHeight - 28, this.mousePositionY + (this.toolbarHeight - 35))) + 'px';
            buttonEl.style.left = Math.max(0, Math.min(this.elementWidth - 30, this.mousePositionX + 6)) + 'px';

            const buttonTextEl = document.createElement('span');
            buttonTextEl.className = 'fa-agpr-ai';
            buttonEl.appendChild(buttonTextEl);
            document.getElementById('agpr_quillTextEditor_element').appendChild(buttonEl);
        }
    }

    clearRefineParagraphButton(): void {
        document.getElementById('agpr_richTextEditor_refinementParagraph')?.remove();
    }

    selectionChange(selection) {
        this.currentSelection = selection;
        if (!this.currentSelection) {
            return;
        }
        const index = this.currentSelection?.range?.index;
        const length = this.currentSelection?.range?.length;
        this.updateSelectionState(index, length);

        if ((this.enableAIHelper || this.enableAIRefinement) && this.openAiPermission) {
            this.addRefineButton();
        }
    }

    /**
     * Gets returns the html that is selected
     */
    getSelectionsHtml(): HtmlSelection {
        const quillSelection = this.quillEditor?.getSelection();
        if (quillSelection && quillSelection.length) {

            //#region -> gets the selection content
            // Gets the contect that is BEFORE the selected text, up to the start of the paragraph
            const preSelectedContent = this.quillEditor.getContents(0, quillSelection.index);

            // Gets the contect of the selected text
            const selectedContent = this.quillEditor.getContents(quillSelection.index, quillSelection.length);

            // Gets the contect that is AFTER the selected text, up to the end of the paragraph
            const postSelectedContent = this.quillEditor.getContents(quillSelection.index + quillSelection.length, this.quillEditor.getText().length);
            //#endregion

            //#region -> convert content to html
            let preSelection = '';
            // pre selection content to html
            if (preSelectedContent?.ops && preSelectedContent?.ops?.length) {

                const preTempContainer = document.createElement('div');
                const preTempQuill = new Quill(preTempContainer);
                preTempQuill.setContents(preSelectedContent);
                // gets rest of paragraph before selection
                const preIndex = preTempContainer.querySelector('.ql-editor')?.innerHTML.lastIndexOf('<p');
                preSelection = preTempContainer.querySelector('.ql-editor')?.innerHTML.substring(preIndex);
            }

            // selected text to html
            let selection = '';
            if (selectedContent?.ops && selectedContent?.ops?.length) {
                const tempContainer = document.createElement('div');
                const tempQuill = new Quill(tempContainer);
                tempQuill.setContents(selectedContent);
                selection = tempContainer.querySelector('.ql-editor')?.innerHTML;
            }

            // post selection content to html
            let postSelection = '';
            if (postSelectedContent?.ops && postSelectedContent?.ops?.length) {
                const postTempContainer = document.createElement('div');
                const postTempQuill = new Quill(postTempContainer);
                postTempQuill.setContents(postSelectedContent);
                // gets rest of paragraph after selection
                const postIndex = postTempContainer.querySelector('.ql-editor')?.innerHTML.indexOf('</p>');
                postSelection = postTempContainer.querySelector('.ql-editor')?.innerHTML.substring(0, postIndex + 4);
            }
            //#endregion
            let selectedContentFlipped = [];
            if (isArray(selectedContent?.ops)) {
                selectedContentFlipped = cloneDeep(selectedContent?.ops).reverse();
            }
            let preSelectedContentFlipped = [];
            if (isArray(preSelectedContent?.ops)) {
                preSelectedContentFlipped = cloneDeep(preSelectedContent?.ops).reverse();
            }

            //#region -> clean up duplicate html elements
            const cleanedUpPostSelections = this.removeDuplicateHtmlElements(selectedContentFlipped, postSelectedContent, postSelection, selection);
            postSelection = cleanedUpPostSelections?.selection;
            selection = cleanedUpPostSelections?.preSelection;

            const cleanedUpPreSelections = this.removeDuplicateHtmlElements(preSelectedContentFlipped, selectedContent, selection, preSelection);
            preSelection = cleanedUpPreSelections?.preSelection;
            selection = cleanedUpPreSelections?.selection;
            //#endregion


            return {
                preSelection: preSelection?.trim(),
                selection: selection?.trim(),
                postSelection: postSelection?.trim(),
            };
        }
        return {
            preSelection: '',
            selection: this.quillEditor?.root?.innerHTML?.trim() || '',
            postSelection: ''
        };
    }

    /**
     * removeDuplicateHtmlElements
     * when selecting the text, if you select the text in the middle of a link, or bold, or styling elements (etc, etc)
     * what happens because you want pre selected, & post selected.. that the selection will "add" extra html elements
     *
     * ex: if you would only select half the text of a bold element... the selections combined would then have them as sepsrate tags, and ruinning the prompts
     * <strong>I love Ottawa</strong>
     * in the above example if you only selected "I love".. the combined selections would make
     * <strong>I love</strong><strong> Ottawa</strong>
     *
     * so this function is to remove the duplicate added html elements after combining the pre, post selections
     *
     * @param contentFlipped is the pre contents but flipped backwards
     * @param selectedContent is the currect contents
     * @param selection is the selection html string with the html tags
     * @param preSelection is the pre selection html string with the html tags
     */
    removeDuplicateHtmlElements(contentFlipped: any[], selectedContent, selection, preSelection): {selection: string; preSelection: string} {
        let duplicateElementStillExists = true;

        // loops through every pre content (starting from the latest since it's flipped)
        contentFlipped.every(() => {
            selectedContent?.ops.every(() => {
                duplicateElementStillExists = true;
                // sometimes for a selection, there will be more than 1 html element, so loop until all duplicates are gone
                while (duplicateElementStillExists) {
                    // get the 2nd html element, ignore the initial <p> tag (but none closing tag)
                    const secondHtmlTagRegex = new RegExp('<[^><]{2,}>', '');
                    const htmlElementMatch = selection?.trim().match(secondHtmlTagRegex);

                    // if the match is at the start of the string (aka the html element is from the previous selection)
                    if (htmlElementMatch?.index <= 3) {
                        // get the html tag ("span", "strong", etc, etc)
                        const htmlTag = htmlElementMatch?.[0]?.trim().match(this.htmlTagRegex);

                        let closingElementRegex = null;
                        if (htmlTag) {
                            // get the LAST closing tag of the html tag
                            closingElementRegex = new RegExp(`<\/${htmlTag[1]}>(?!.*<\/${htmlTag[1]}>)`, '');

                            const closingElementMatch = preSelection?.trim().match(closingElementRegex);

                            // if there is a closing tag that matches the html tag selected
                            if (closingElementMatch) {
                                // remove the html element of the selection html text
                                selection = selection?.trim()?.replace(secondHtmlTagRegex, '');
                                // remove the closing tag of the pre selection html text
                                preSelection = preSelection?.trim()?.replace(closingElementRegex, '');
                            } else {
                                duplicateElementStillExists = false;
                            }
                        } else {
                            duplicateElementStillExists = false;
                        }
                    } else {
                        duplicateElementStillExists = false;
                        return false;
                    }
                }
                return true;
            });
            return true;
        });

        // since we have to do this function twice, and we cannot pass a string by reference, I made the function return the updated strings
        return {
            selection, preSelection
        };
    }

    updateSelectionState(index: number, length: number): void {
        if (!this.quillEditor || (!index && index !== 0) || (!length && length !== 0)) {
            return;
        }
        const format = this.quillEditor.getFormat(index, length);

        this.isBold = format?.bold ? format.bold : false;
        this.isItalic = format?.italic ? format.italic : false;
        this.isUnderlined = format?.underline ? format.underline : false;
        this.isStrikethrough = format?.strike ? format.strike : false;
        this.isRTL = format?.direction === 'rtl';
        this.isBlockQuote = format?.blockQuote ? format.blockQuote : false;

        const defaultSelectColor = this.configService.isNewBranding() ? '#1e1e1e' : '#292929';
        this.selectedColor = format?.color ? format.color : defaultSelectColor;

        this.selectedFontBackgroundColor = format?.background ? format.background : null;
        this.selectedScript = format?.script ? format.script : null;
        this.selectedListType = format?.list ? format.list : null;
        this.selectedAlignment = format?.align ? format.align : null;
        this.selectedLink = format?.link ? format.link : null;
    }

    editLink(unused: any, url: string) {
        // clicking on edit will update selection range to null, so use oldRange here
        const range = this.getLinkRangeFromOffset(this.currentSelection.oldRange.index);
        const ioModel = this.getLinkConfig(range, url);
        this.coreDialogService.open(LinkDialogComponent, ioModel);
    }

    launchAIHelperDialog() {
        const ioModel = this.getAIHelperConfig();
        this.coreDialogService.open(AIHelperDialogComponent, ioModel, this.injector);
    }

    launchAIRefinementDialog(buttonElement?: any) {
        this.removeHighlightParagraph();
        const ioModel = this.getAIRefinementConfig(buttonElement);
        this.refinementModalOpen = true;
        if (buttonElement) {
            this.coreDialogService.open(AIRefinementPopupComponent, ioModel, this.injector, 'AIRefinementPopup');
        } else {
            this.coreDialogService.open(AIRefinementDialogComponent, ioModel, this.injector);
        }
    }

    private getAIRefinementConfig(buttonElement?: any): DialogIoModel {

        const selections = this.getSelectionsHtml();

        let preSelectionHtml = selections?.preSelection;

        // if pre select has text, stripe last 4 characters "</p>" (it will always be </p>)
        if (!this.emptyParagraphMatch.includes(preSelectionHtml)) {
            preSelectionHtml = preSelectionHtml.substring(0, preSelectionHtml.length-4);
        } else {
            preSelectionHtml = null;
        }

        // strip out the starting P tag
        let postSelectionHtml = selections?.postSelection?.replace(this.pTagRegex, '');
        if (this.emptyParagraphMatch.includes(postSelectionHtml)) {
            postSelectionHtml = null;
        }

        let selectionHtml = selections?.selection;
        // if pre select has text, stripe first 3 characters "<p>" (it will always be <p>
        if (preSelectionHtml && (preSelectionHtml !== '')) {
            selectionHtml = selectionHtml.replace(this.pTagRegex, '');
        }
        // if post select has text, stripe last 4 characters "</p>" (it will always be </p>)
        if (postSelectionHtml && (postSelectionHtml !== '') && selectionHtml.includes('</p>')) {
            selectionHtml = selectionHtml.substring(0, selectionHtml.length-4);
        }

        const positionX = Math.min(buttonElement?.getBoundingClientRect()?.x || 0, window.innerWidth - this.aiPopupWidth);
        // we don't want to popup window to go outside
        const positionY = Math.min(buttonElement?.getBoundingClientRect()?.y || 0, window.innerHeight - this.aiPopupHeight);

        const ioModel: DialogIoModel = {
            inputs: {
                overlayContainer: this.overlayContainer,
                selectionHtml: selectionHtml,
                preSelectionHtml: preSelectionHtml,
                postSelectionHtml: postSelectionHtml,
                aiReferenceId: this.aiReferenceId,
                style: AgprComponentStyleEnum.social,
                positionX: positionX,
                positionY: positionY,
            },
            outputs: {
                dialogClose: (action: any) => {
                    const currentPosition = this.currentSelection?.range?.index ?? this.currentSelection?.oldRange?.index ?? 0;
                    const currentLength = this.currentSelection?.range?.length ?? this.currentSelection?.oldRange?.length ?? 0;

                    if (action) {
                        if (action.action === 'replace') {
                            if (currentLength === 0) {
                                // Delete every thing
                                this.quillEditor.setText('', 'user');
                                this.quillEditor.pasteHTML(0, action.generatedContent, 'user');
                                this.quillEditor.setSelection(0, this.quillEditor.getLength(), 'user');
                            } else {
                                // HACK (listCount): seems that whenever there is a list, the htmlToText does not add a new line after every
                                // list & break line in empty paragraphs which in turn makes the selection off by every list item in the selection
                                let listCount = Math.max((action.generatedContent.match(/<li/g) || []).length - 1, 0);
                                listCount += (Math.max((action.generatedContent.match(/><br></g) || []).length - 1, 0) * 2);

                                this.quillEditor.deleteText(currentPosition, currentLength, 'user');
                                this.quillEditor.pasteHTML(currentPosition, action.generatedContent, 'user');
                                this.quillEditor.setSelection(currentPosition, (htmlToText(action.generatedContent).length + listCount) || 0, 'user');
                            }
                            this.quillEditor.focus();
                        } else if (action.action === 'insert') {
                            this.insertTextBlock.emit(action.generatedContent);
                        }
                        this.change();
                        this.tooltip.hide();
                    } else {
                        this.quillEditor.setSelection(currentPosition, currentLength, 'user');
                    }
                    this.refinementModalOpen = false;
                }
            }
        };

        return ioModel;
    }

    setLink() {
        let range = this.currentSelection?.range;
        if ((!range || range.length === 0) && this.selectedLink && this.currentSelection.range) {
            range = this.getLinkRangeFromOffset(this.currentSelection.range.index);
        }
        if (!range || range.length === 0) {
            return;
        }
        const ioModel = this.getLinkConfig(range, this.selectedLink);
        this.coreDialogService.open(LinkDialogComponent, ioModel);
    }

    private getLinkConfig(range: Range, url?: string) {
        const initialLink = !!url ? new LinkModel().decodeUrl(url) : new LinkModel();
        const ioModel = {
            inputs: {
                heading: 'Common.dialog.linkType.insertLink',
                link: initialLink,
                availableLinkTypes: [
                    new DropdownOptionModel('Common.dialog.linkType.webAddress', LinkTypeEnum.URL),
                    new DropdownOptionModel( 'Common.dialog.linkType.emailAddress', LinkTypeEnum.EMAIL)
                ],
                isLinkField: true
            },
            outputs: {
                dialogClose: (link: LinkModel) => {
                    if (link) {
                        this.quillEditor.formatText(range.index, range.length, 'link', link.url || link.mailTo, 'user');
                    }
                },
                removeLink: () => {
                    this.quillEditor.formatText(range, 'link', false, 'user');
                    this.tooltip.hide();
                }
            }
        };
        return ioModel;
    }

    /**
     * Calculates the entire range of a given link selection. Used when a selection of a link
     * is only partial, or on single click
     */
    private getLinkRangeFromOffset(index: number): Range {
        const [link, offset] = this.quillEditor.scroll.descendant(LINK, index);
        if (!link) {
            return null;
        }
        return { index: (index - offset), length: link.length() };
    }

    private getAIHelperConfig(): DialogIoModel {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const ioModel: DialogIoModel = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                title: 'Common.dialog.aiHelper.title',
                aiReferenceId: this.aiReferenceId
            },
            outputs: {
                dialogClose: (text: string) => {
                    if (text) {
                        this.quillEditor.setText(text, 'user');
                        this.change();
                        this.tooltip.hide();
                    }
                }
            }
        };

        return ioModel;
    }

    setColorProperty(property: string) {
        let initialColor = this.selectedColor ? this.selectedColor : this.formatOptions.color;
        if (property === 'background') {
            initialColor = this.selectedFontBackgroundColor ? this.selectedFontBackgroundColor : '#ffffff';
        }

        const ioModel = {
            inputs: {
                heading: 'Select Color',
                initialValue: ColorModel.createFromHex(initialColor),
                submit: (selectedColor: ColorModel) => {
                    const fontColor = selectedColor.hex;
                    const rgb = ColorModel.HEXtoRGB(fontColor);
                    this.quillEditor.format(property, `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`, 'user');
                    if (property === 'background') {
                        this.selectedFontBackgroundColor = selectedColor.hex;
                    } else {
                        this.selectedColor = selectedColor.hex;
                    }

                    return new BehaviorSubject<string>(fontColor);
                }
            },
            outputs: {}
        };
        this.coreDialogService.open(ColorPickerDialogComponent, ioModel);
    }

    setAlignment(value: string) {
        if (!this.quillEditor) {
            return;
        }
        this.selectedAlignment = value;
        this.quillEditor.format('align', value, 'user');
    }

    setMergeTag(mergeTag: DropdownOptionModel) {
        this.quillEditor.focus();
        const cursorPosition = this.quillEditor?.getSelection()?.index || 0;
        const newCursorPos = cursorPosition + mergeTag.value.length;
        this.quillEditor.insertText(cursorPosition, mergeTag.value, 'user');
        this.quillEditor.setSelection(newCursorPos, 'silent');

    }

    showSwitchEditingModeWarning() {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                heading: 'Common.forms.richText.switchToHTMLEdit.header',
                messageHtml: 'Common.forms.richText.switchToHTMLEdit.possible.message',
                okLabel: 'Common.forms.richText.switchToHTMLEdit.affirmation',
                alertMessage: 'Common.forms.richText.switchToHTMLEdit.infoBanner',
                alertType: messageSeverityEnum.warning
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.changeToHtmlEditor();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Handles custom File uploade button
     */
    public handleFileInput(file: File, imageInput: HTMLInputElement): void {
        imageInput.value = '';

        if (!file.type.match(IMAGE_FILE_TYPE_REGEX)) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                detail: this.localizationService.transform(
                    'PageBuilder.error.fileTypeMessage',
                    {
                        types: 'gif, jpg, png'
                    }
                )
            });

            return;
        }

        if (MAX_FILE_SIZE_BYTES < file.size) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage.specific', {
                    size: getReadableFileSize(MAX_FILE_SIZE_BYTES)
                })
            });
            return;
        }

        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).subscribe({
            next: result => {
                const imageMediaResult: MediaResultModel = result?.mediaResults?.find(mediaResult => mediaResult?.media?.name === file.name);

                if (imageMediaResult) {
                    const url = imageMediaResult?.media?.url;
                    const errorCode = imageMediaResult?.codedError?.code;

                    if (errorCode) {
                        this.handleErrorCode(errorCode);

                        return;
                    }

                    const range = this.quillEditor.getSelection(true);
                    this.quillEditor.insertEmbed(range.index, 'image', url, 'user');
                    this.quillEditor.setSelection(
                        {
                            index: range.index + 1,
                            length: range.length
                        },
                        'api'
                    );
                }
            },
            error: (error) => {
                if (error.status === 429) {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                    });
                }
            }
        });
    }

    handleErrorCode(errorCode: MediaServiceErrorCodesEnum) {
        if (errorCode === MediaServiceErrorCodesEnum.insufficientSpace) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'PageBuilder.error.imageLimitHeader',
                    messageHtml: 'PageBuilder.error.imageLimitMessageHtml',
                    alertType: messageSeverityEnum.error,
                    okLabel: 'PageBuilder.error.imageLimitOkLabel',
                    showCancel: false
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {

                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            return;
        }

        if (errorCode === MediaServiceErrorCodesEnum.fileMaxSize) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage')
            });
            return;
        }

        if (errorCode !== null) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.imageUploadFailed'),
                detail: this.localizationService.transform('PageBuilder.error.imageUploadFailedMessage')
            });
        }
    }

    formatHTML(str: string) {
        if (!str) {
            return null;
        }

        const div = document.createElement('div');
        div.innerHTML = str.trim();

        // use slice to remove new lines from start and end
        return this.formatNode(div, 0).innerHTML.slice(1, -1);
    }

    formatNode(node, level) {
        const indentWidth = '    ';
        const indentBefore = new Array(level++ + 1).join(indentWidth);
        const indentAfter  = new Array(level - 1).join(indentWidth);
        let textNode;

        for (let i = 0; i < node.children.length; i++) {
            textNode = document.createTextNode('\n' + indentBefore);
            node.insertBefore(textNode, node.children[i]);
            this.formatNode(node.children[i], level);
            if (node.lastElementChild === node.children[i]) {
                textNode = document.createTextNode('\n' + indentAfter);
                node.appendChild(textNode);
            }
        }

        return node;
    }

    handleHistory(action: ('undo' | 'redo')) {
        if (action === 'undo') {
            this.quillEditor.history.undo();
        } else {
            this.quillEditor.history.redo();
        }
    }

    updateListAfterString(searchString: string) {
        this.selectedLanguage = new DropdownOptionModel(searchString, searchString);
    }

    translate() {
        this.translation.originalContent = this.value;
        this.translation.setIsTranslating(true);

        if (isEmpty(this.selectedLanguage.value)) {
            this.selectedLanguage = {...this.defaultLanguage};
        }

        this.translateService.setupChatAIForText(this.translation, this.selectedLanguage);

        this.translation.isTranslating$.pipe(
            takeWhile(isTranslating => {

                if (!isTranslating) {
                    this.value = this.translation.translatedContent;
                    this.change();
                }

                this.isTranslating = isTranslating;
                return isTranslating;
            })
        ).subscribe();
    }

    openEditMergeTag() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
            }
        };

        this.coreDialogService.open(FallbackPersonalizationsDialogComponent, io);
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        if (this.mouseMoveListener) {
            this.mouseMoveListener();
            this.mouseMoveListener = null;
        }
    }
}
